// src/components/admin/page-management/section-forms/HeaderDividedForm.js
import React, { useState } from "react";
import { Info } from "lucide-react";
import ColorPicker from "../../shared/ColorPicker";
import MediaLibrary from "../../shared/MediaUploader";

const HeaderDividedForm = ({ content, updateContent, pageSlug }) => {
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [currentImageField, setCurrentImageField] = useState(null);
  const [currentMediaType, setCurrentMediaType] = useState("image");

  const handleSelectImage = (imageUrl) => {
    if (currentImageField) {
      updateContent(currentImageField, imageUrl);
      setShowMediaLibrary(false);
      setCurrentImageField(null);
    }
  };

  const openMediaLibrary = (fieldName, mediaType = "image") => {
    setCurrentImageField(fieldName);
    setCurrentMediaType(mediaType);
    setShowMediaLibrary(true);
  };

  // Helper for adding/removing category items
  const handleCategoryChange = (index, field, value) => {
    const newCategories = [...(content.categoryItems || [])];
    if (!newCategories[index]) {
      newCategories[index] = {};
    }
    newCategories[index][field] = value;
    updateContent("categoryItems", newCategories);
  };

  const addCategoryItem = () => {
    const newCategories = [...(content.categoryItems || [])];
    newCategories.push({ name: "", imageUrl: "", link: "" });
    updateContent("categoryItems", newCategories);
  };

  const removeCategoryItem = (index) => {
    const newCategories = [...(content.categoryItems || [])];
    newCategories.splice(index, 1);
    updateContent("categoryItems", newCategories);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Header Divided Section</h3>

      {showMediaLibrary ? (
        <div className="bg-white rounded-lg shadow-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium">Media Library</h4>
            <button
              onClick={() => setShowMediaLibrary(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              Close
            </button>
          </div>
          <MediaLibrary
            onUploadComplete={handleSelectImage}
            type={currentMediaType}
            pageSlug={pageSlug}
          />
        </div>
      ) : (
        <>
          {/* Main Content Settings */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-medium mb-4">Content Settings</h4>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Title</label>
              <textarea
                value={
                  content?.title ||
                  "Freshly <span>Baked,<br/> Daily</span> Served"
                }
                onChange={(e) => updateContent("title", e.target.value)}
                className="w-full p-2 border rounded"
                rows="3"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use &lt;span&gt;text&lt;/span&gt; to highlight parts of the
                title and &lt;br/&gt; for line breaks
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Subtitle</label>
              <textarea
                value={
                  content?.subtitle ||
                  "Our bakery and pastry products are produced each day for the best quality and taste."
                }
                onChange={(e) => updateContent("subtitle", e.target.value)}
                className="w-full p-2 border rounded"
                rows="3"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Primary Button Text
                </label>
                <input
                  type="text"
                  value={content?.primaryButtonText || "View Our Menu"}
                  onChange={(e) =>
                    updateContent("primaryButtonText", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Primary Button Link
                </label>
                <input
                  type="text"
                  value={content?.primaryButtonLink || "/menu"}
                  onChange={(e) =>
                    updateContent("primaryButtonLink", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Secondary Button Text
                </label>
                <input
                  type="text"
                  value={content?.secondaryButtonText || "Order Online"}
                  onChange={(e) =>
                    updateContent("secondaryButtonText", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Secondary Button Link
                </label>
                <input
                  type="text"
                  value={
                    content?.secondaryButtonLink ||
                    "https://anorder.ca/1781/paneformaggio"
                  }
                  onChange={(e) =>
                    updateContent("secondaryButtonLink", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Third Button Text
                </label>
                <input
                  type="text"
                  value={content?.thirdButtonText || ""}
                  onChange={(e) =>
                    updateContent("thirdButtonText", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                  placeholder="Optional third button"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Third Button Link
                </label>
                <input
                  type="text"
                  value={content?.thirdButtonLink || ""}
                  onChange={(e) =>
                    updateContent("thirdButtonLink", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                  placeholder="/third-button-url"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Fourth Button Text
                </label>
                <input
                  type="text"
                  value={content?.fourthButtonText || ""}
                  onChange={(e) =>
                    updateContent("fourthButtonText", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                  placeholder="Optional fourth button"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Fourth Button Link
                </label>
                <input
                  type="text"
                  value={content?.fourthButtonLink || ""}
                  onChange={(e) =>
                    updateContent("fourthButtonLink", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                  placeholder="/fourth-button-url"
                />
              </div>
            </div>

            <div>
              <p className="mb-4 mt-2 font-bold">Buttons Icons </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">
                  Button Icon Position
                </label>
                <select
                  value={content?.buttonIconPosition || "before-text"}
                  onChange={(e) =>
                    updateContent("buttonIconPosition", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                >
                  <option value="before-text">Before Text (Left)</option>
                  <option value="top-center">Top Center</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium mb-1">
                  Button Icon Size
                </label>
                <input
                  type="text"
                  value={content?.buttonIconSize || "20px"}
                  onChange={(e) =>
                    updateContent("buttonIconSize", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                  placeholder="e.g. 20px"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Primary Button Icon
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={content?.primaryButtonIcon || ""}
                    onChange={(e) =>
                      updateContent("primaryButtonIcon", e.target.value)
                    }
                    className="w-full p-2 border rounded"
                    placeholder="e.g. /images/icons/cart.png"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      openMediaLibrary("primaryButtonIcon", "image")
                    }
                    className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Browse
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Secondary Button Icon
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={content?.secondaryButtonIcon || ""}
                    onChange={(e) =>
                      updateContent("secondaryButtonIcon", e.target.value)
                    }
                    className="w-full p-2 border rounded"
                    placeholder="e.g. /images/icons/phone.png"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      openMediaLibrary("secondaryButtonIcon", "image")
                    }
                    className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Browse
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Third Button Icon
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={content?.thirdButtonIcon || ""}
                    onChange={(e) =>
                      updateContent("thirdButtonIcon", e.target.value)
                    }
                    className="w-full p-2 border rounded"
                    placeholder="e.g. /images/icons/email.png"
                  />
                  <button
                    type="button"
                    onClick={() => openMediaLibrary("thirdButtonIcon", "image")}
                    className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Browse
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Fourth Button Icon
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={content?.fourthButtonIcon || ""}
                    onChange={(e) =>
                      updateContent("fourthButtonIcon", e.target.value)
                    }
                    className="w-full p-2 border rounded"
                    placeholder="e.g. /images/icons/location.png"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      openMediaLibrary("fourthButtonIcon", "image")
                    }
                    className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Browse
                  </button>
                </div>
              </div>
            </div>

            {/* Primary Button Background Settings */}
            <div className="mb-6 border-b pb-4">
              <h5 className="font-medium mb-2">Primary Button</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="mb-2">
                  <label className="block text-sm font-medium mb-1">
                    Background Type
                  </label>
                  <select
                    value={content?.primaryButtonBgType || "solid"}
                    onChange={(e) =>
                      updateContent("primaryButtonBgType", e.target.value)
                    }
                    className="w-full p-2 border rounded"
                  >
                    <option value="solid">Solid Color</option>
                    <option value="transparent">Transparent</option>
                    <option value="gradient">Gradient</option>
                  </select>
                </div>

                {content?.primaryButtonBgType !== "transparent" && (
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Opacity
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      className="w-full"
                      value={content?.primaryButtonOpacity || "1"}
                      onChange={(e) =>
                        updateContent("primaryButtonOpacity", e.target.value)
                      }
                    />
                    <div className="flex justify-between text-xs mt-1">
                      <span>Transparent</span>
                      <span>Opaque</span>
                    </div>
                  </div>
                )}
              </div>

              {content?.primaryButtonBgType === "gradient" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Gradient From
                    </label>
                    <ColorPicker
                      color={
                        content?.primaryButtonGradientFrom ||
                        content?.primaryButtonBackgroundColor ||
                        "#d17b34"
                      }
                      onChange={(color) =>
                        updateContent("primaryButtonGradientFrom", color)
                      }
                    />
                  </div>
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Gradient To
                    </label>
                    <ColorPicker
                      color={content?.primaryButtonGradientTo || "#e8924a"}
                      onChange={(color) =>
                        updateContent("primaryButtonGradientTo", color)
                      }
                    />
                  </div>
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Gradient Direction
                    </label>
                    <select
                      value={
                        content?.primaryButtonGradientDirection || "to right"
                      }
                      onChange={(e) =>
                        updateContent(
                          "primaryButtonGradientDirection",
                          e.target.value
                        )
                      }
                      className="w-full p-2 border rounded"
                    >
                      <option value="to right">Left to Right</option>
                      <option value="to left">Right to Left</option>
                      <option value="to bottom">Top to Bottom</option>
                      <option value="to top">Bottom to Top</option>
                      <option value="to bottom right">
                        Top Left to Bottom Right
                      </option>
                      <option value="to bottom left">
                        Top Right to Bottom Left
                      </option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Secondary Button Background Settings */}
            <div className="mb-6 border-b pb-4">
              <h5 className="font-medium mb-2">Secondary Button</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="mb-2">
                  <label className="block text-sm font-medium mb-1">
                    Background Type
                  </label>
                  <select
                    value={content?.secondaryButtonBgType || "transparent"}
                    onChange={(e) =>
                      updateContent("secondaryButtonBgType", e.target.value)
                    }
                    className="w-full p-2 border rounded"
                  >
                    <option value="solid">Solid Color</option>
                    <option value="transparent">Transparent</option>
                    <option value="gradient">Gradient</option>
                  </select>
                </div>

                {content?.secondaryButtonBgType !== "transparent" && (
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Opacity
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      className="w-full"
                      value={content?.secondaryButtonOpacity || "1"}
                      onChange={(e) =>
                        updateContent("secondaryButtonOpacity", e.target.value)
                      }
                    />
                    <div className="flex justify-between text-xs mt-1">
                      <span>Transparent</span>
                      <span>Opaque</span>
                    </div>
                  </div>
                )}

                {content?.secondaryButtonBgType === "solid" && (
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Background Color
                    </label>
                    <ColorPicker
                      color={
                        content?.secondaryButtonBackgroundColor || "transparent"
                      }
                      onChange={(color) =>
                        updateContent("secondaryButtonBackgroundColor", color)
                      }
                    />
                  </div>
                )}
              </div>

              {content?.secondaryButtonBgType === "gradient" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Gradient From
                    </label>
                    <ColorPicker
                      color={
                        content?.secondaryButtonGradientFrom ||
                        content?.secondaryButtonBorderColor ||
                        "#d17b34"
                      }
                      onChange={(color) =>
                        updateContent("secondaryButtonGradientFrom", color)
                      }
                    />
                  </div>
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Gradient To
                    </label>
                    <ColorPicker
                      color={content?.secondaryButtonGradientTo || "#e8924a"}
                      onChange={(color) =>
                        updateContent("secondaryButtonGradientTo", color)
                      }
                    />
                  </div>
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Gradient Direction
                    </label>
                    <select
                      value={
                        content?.secondaryButtonGradientDirection || "to right"
                      }
                      onChange={(e) =>
                        updateContent(
                          "secondaryButtonGradientDirection",
                          e.target.value
                        )
                      }
                      className="w-full p-2 border rounded"
                    >
                      <option value="to right">Left to Right</option>
                      <option value="to left">Right to Left</option>
                      <option value="to bottom">Top to Bottom</option>
                      <option value="to top">Bottom to Top</option>
                      <option value="to bottom right">
                        Top Left to Bottom Right
                      </option>
                      <option value="to bottom left">
                        Top Right to Bottom Left
                      </option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Third Button Background Settings (if needed) */}
            {content?.thirdButtonText && (
              <div className="mb-6 border-b pb-4">
                <h5 className="font-medium mb-2">Third Button</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Background Type
                    </label>
                    <select
                      value={content?.thirdButtonBgType || "transparent"}
                      onChange={(e) =>
                        updateContent("thirdButtonBgType", e.target.value)
                      }
                      className="w-full p-2 border rounded"
                    >
                      <option value="solid">Solid Color</option>
                      <option value="transparent">Transparent</option>
                      <option value="gradient">Gradient</option>
                    </select>
                  </div>

                  {content?.thirdButtonBgType !== "transparent" && (
                    <div className="mb-2">
                      <label className="block text-sm font-medium mb-1">
                        Opacity
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        className="w-full"
                        value={content?.thirdButtonOpacity || "1"}
                        onChange={(e) =>
                          updateContent("thirdButtonOpacity", e.target.value)
                        }
                      />
                      <div className="flex justify-between text-xs mt-1">
                        <span>Transparent</span>
                        <span>Opaque</span>
                      </div>
                    </div>
                  )}

                  {content?.thirdButtonBgType === "solid" && (
                    <div className="mb-2">
                      <label className="block text-sm font-medium mb-1">
                        Background Color
                      </label>
                      <ColorPicker
                        color={
                          content?.thirdButtonBackgroundColor || "transparent"
                        }
                        onChange={(color) =>
                          updateContent("thirdButtonBackgroundColor", color)
                        }
                      />
                    </div>
                  )}
                </div>

                {content?.thirdButtonBgType === "gradient" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <div className="mb-2">
                      <label className="block text-sm font-medium mb-1">
                        Gradient From
                      </label>
                      <ColorPicker
                        color={
                          content?.thirdButtonGradientFrom ||
                          content?.secondaryButtonBorderColor ||
                          "#d17b34"
                        }
                        onChange={(color) =>
                          updateContent("thirdButtonGradientFrom", color)
                        }
                      />
                    </div>
                    <div className="mb-2">
                      <label className="block text-sm font-medium mb-1">
                        Gradient To
                      </label>
                      <ColorPicker
                        color={content?.thirdButtonGradientTo || "#e8924a"}
                        onChange={(color) =>
                          updateContent("thirdButtonGradientTo", color)
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Fourth Button Background Settings (if needed) */}
            {content?.fourthButtonText && (
              <div className="mb-6">
                <h5 className="font-medium mb-2">Fourth Button</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-2">
                    <label className="block text-sm font-medium mb-1">
                      Background Type
                    </label>
                    <select
                      value={content?.fourthButtonBgType || "solid"}
                      onChange={(e) =>
                        updateContent("fourthButtonBgType", e.target.value)
                      }
                      className="w-full p-2 border rounded"
                    >
                      <option value="solid">Solid Color</option>
                      <option value="transparent">Transparent</option>
                      <option value="gradient">Gradient</option>
                    </select>
                  </div>

                  {content?.fourthButtonBgType !== "transparent" && (
                    <div className="mb-2">
                      <label className="block text-sm font-medium mb-1">
                        Opacity
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        className="w-full"
                        value={content?.fourthButtonOpacity || "1"}
                        onChange={(e) =>
                          updateContent("fourthButtonOpacity", e.target.value)
                        }
                      />
                      <div className="flex justify-between text-xs mt-1">
                        <span>Transparent</span>
                        <span>Opaque</span>
                      </div>
                    </div>
                  )}

                  {content?.fourthButtonBgType === "solid" && (
                    <div className="mb-2">
                      <label className="block text-sm font-medium mb-1">
                        Background Color
                      </label>
                      <ColorPicker
                        color={
                          content?.fourthButtonBackgroundColor ||
                          content?.primaryButtonBackgroundColor ||
                          "#d17b34"
                        }
                        onChange={(color) =>
                          updateContent("fourthButtonBackgroundColor", color)
                        }
                      />
                    </div>
                  )}
                </div>

                {content?.fourthButtonBgType === "gradient" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <div className="mb-2">
                      <label className="block text-sm font-medium mb-1">
                        Gradient From
                      </label>
                      <ColorPicker
                        color={
                          content?.fourthButtonGradientFrom ||
                          content?.primaryButtonBackgroundColor ||
                          "#d17b34"
                        }
                        onChange={(color) =>
                          updateContent("fourthButtonGradientFrom", color)
                        }
                      />
                    </div>
                    <div className="mb-2">
                      <label className="block text-sm font-medium mb-1">
                        Gradient To
                      </label>
                      <ColorPicker
                        color={content?.fourthButtonGradientTo || "#e8924a"}
                        onChange={(color) =>
                          updateContent("fourthButtonGradientTo", color)
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Hours Badge Text
              </label>
              <input
                type="text"
                value={
                  content?.hoursText ||
                  "Open Today: <span>7:00 AM - 10:00 PM</span>"
                }
                onChange={(e) => updateContent("hoursText", e.target.value)}
                className="w-full p-2 border rounded"
              />
              <p className="text-xs text-gray-500 mt-1">
                Use &lt;span&gt;text&lt;/span&gt; to highlight part of the text
              </p>
            </div>

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.iconVisible !== false}
                  onChange={(e) =>
                    updateContent("iconVisible", e.target.checked)
                  }
                  className="mr-2"
                />
                <span className="text-sm">Show Icon</span>
              </label>
            </div>

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.hoursBadgeVisible !== false}
                  onChange={(e) =>
                    updateContent("hoursBadgeVisible", e.target.checked)
                  }
                  className="mr-2"
                />
                <span className="text-sm">Show Hours Badge</span>
              </label>
            </div>
          </div>

          {/* Image Settings */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-medium mb-4">Image Settings</h4>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Main Image
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={content?.mainImage || "/images/pastry/bread-hero.webp"}
                  onChange={(e) => updateContent("mainImage", e.target.value)}
                  className="w-full p-2 border rounded"
                />
                <button
                  type="button"
                  onClick={() => openMediaLibrary("mainImage", "image")}
                  className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Browse
                </button>
              </div>
              {content?.mainImage && (
                <div className="mt-2 relative h-20 w-full overflow-hidden rounded border">
                  <img
                    src={content.mainImage}
                    alt="Main Image Preview"
                    className="h-full w-full object-cover"
                  />
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Company Icon
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={content?.breadIcon || "/images/pastry/bread-icon.png"}
                  onChange={(e) => updateContent("breadIcon", e.target.value)}
                  className="w-full p-2 border rounded"
                />
                <button
                  type="button"
                  onClick={() => openMediaLibrary("breadIcon", "image")}
                  className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Browse
                </button>
              </div>
              {content?.breadIcon && (
                <div className="mt-2 relative h-20 w-20 overflow-hidden rounded border">
                  <img
                    src={content.breadIcon}
                    alt="Icon Preview"
                    className="h-full w-full object-contain"
                  />
                </div>
              )}
            </div>

            {/* Add this section after leftBackgroundColor control in the form */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Left Section Background Type
              </label>
              <select
                value={content?.leftSectionType || "color"}
                onChange={(e) =>
                  updateContent("leftSectionType", e.target.value)
                }
                className="w-full p-2 border rounded"
              >
                <option value="color">Color Background</option>
                <option value="image">Image Background</option>
                <option value="video">Video Background</option>
              </select>
            </div>

            {content?.leftSectionType === "image" && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Left Background Image
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={content?.leftBackgroundImage || ""}
                    onChange={(e) =>
                      updateContent("leftBackgroundImage", e.target.value)
                    }
                    className="w-full p-2 border rounded"
                    placeholder="Enter image URL"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      openMediaLibrary("leftBackgroundImage", "image")
                    }
                    className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Browse
                  </button>
                </div>
                {content?.leftBackgroundImage && (
                  <div className="mt-2 relative h-20 w-full overflow-hidden rounded border">
                    <img
                      src={content.leftBackgroundImage}
                      alt="Left Background Preview"
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
              </div>
            )}

            {content?.leftSectionType === "video" && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Left Background Video
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={content?.leftBackgroundVideo || ""}
                    onChange={(e) =>
                      updateContent("leftBackgroundVideo", e.target.value)
                    }
                    className="w-full p-2 border rounded"
                    placeholder="Enter video URL or upload"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      openMediaLibrary("leftBackgroundVideo", "video")
                    }
                    className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Browse
                  </button>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Supported formats: MP4, WebM, OGG (max 100MB)
                </div>
              </div>
            )}

            {(content?.leftSectionType === "image" ||
              content?.leftSectionType === "video") && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Content Overlay
                  </label>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={content?.leftOverlayEnabled || false}
                      onChange={(e) =>
                        updateContent("leftOverlayEnabled", e.target.checked)
                      }
                      className="mr-2"
                    />
                    <span className="text-sm">
                      Enable overlay for better text visibility
                    </span>
                  </div>
                </div>

                {content?.leftOverlayEnabled && (
                  <>
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1">
                        Overlay Color
                      </label>
                      <ColorPicker
                        color={
                          content?.leftOverlayColor ||
                          "rgba(248, 243, 233, 0.8)"
                        }
                        onChange={(color) =>
                          updateContent("leftOverlayColor", color)
                        }
                        allowAlpha={true}
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1">
                        Overlay Opacity
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        className="w-full"
                        value={content?.leftOverlayOpacity || "0.8"}
                        onChange={(e) =>
                          updateContent("leftOverlayOpacity", e.target.value)
                        }
                      />
                      <div className="flex justify-between text-xs">
                        <span>Transparent</span>
                        <span>Opaque</span>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Floating Image 1
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={
                      content?.floatingImage1 || "/images/pastry/croissant.png"
                    }
                    onChange={(e) =>
                      updateContent("floatingImage1", e.target.value)
                    }
                    className="w-full p-2 border rounded"
                  />
                  <button
                    type="button"
                    onClick={() => openMediaLibrary("floatingImage1", "image")}
                    className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Browse
                  </button>
                </div>
                {content?.floatingImage1 && (
                  <div className="mt-2 relative h-20 w-20 overflow-hidden rounded border">
                    <img
                      src={content.floatingImage1}
                      alt="Floating Image 1 Preview"
                      className="h-full w-full object-contain"
                    />
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Floating Image 2
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={
                      content?.floatingImage2 || "/images/pastry/bread.png"
                    }
                    onChange={(e) =>
                      updateContent("floatingImage2", e.target.value)
                    }
                    className="w-full p-2 border rounded"
                  />
                  <button
                    type="button"
                    onClick={() => openMediaLibrary("floatingImage2", "image")}
                    className="ml-2 px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Browse
                  </button>
                </div>
                {content?.floatingImage2 && (
                  <div className="mt-2 relative h-20 w-20 overflow-hidden rounded border">
                    <img
                      src={content.floatingImage2}
                      alt="Floating Image 2 Preview"
                      className="h-full w-full object-contain"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.floatingImage1Visible !== false}
                  onChange={(e) =>
                    updateContent("floatingImage1Visible", e.target.checked)
                  }
                  className="mr-2"
                />
                <span className="text-sm">Show Floating Image 1</span>
              </label>
            </div>

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.floatingImage2Visible !== false}
                  onChange={(e) =>
                    updateContent("floatingImage2Visible", e.target.checked)
                  }
                  className="mr-2"
                />
                <span className="text-sm">Show Floating Image 2</span>
              </label>
            </div>
          </div>

          {/* Button Style Controls */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Button Style
            </label>
            <select
              value={content?.buttonStyle || "default"}
              onChange={(e) => updateContent("buttonStyle", e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="default">Default (Rounded Pills)</option>
              <option value="square">Square (Large)</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Button Height
            </label>
            <input
              type="text"
              value={content?.buttonHeight || ""}
              onChange={(e) => updateContent("buttonHeight", e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="e.g., 64px (leave empty for default)"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Mobile Button Layout
            </label>
            <select
              value={content?.mobileButtonLayout || "horizontal"}
              onChange={(e) =>
                updateContent("mobileButtonLayout", e.target.value)
              }
              className="w-full p-2 border rounded"
            >
              <option value="horizontal">Horizontal</option>
              <option value="vertical">Vertical</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Button Layout
            </label>
            <select
              value={content?.buttonLayoutExtended || "default"}
              onChange={(e) =>
                updateContent("buttonLayoutExtended", e.target.value)
              }
              className="w-full p-2 border rounded"
            >
              <option value="default">Default (Left side only)</option>
              <option value="extended">Extended (Across both sides)</option>
            </select>
          </div>

          {/* Color Settings */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-medium mb-4">Color Settings</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Left Background Color
                </label>
                <ColorPicker
                  color={content?.leftBackgroundColor || "#f8f3e9"}
                  onChange={(color) =>
                    updateContent("leftBackgroundColor", color)
                  }
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Right Background Color
                </label>
                <ColorPicker
                  color={content?.rightBackgroundColor || "#5c3618"}
                  onChange={(color) =>
                    updateContent("rightBackgroundColor", color)
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Title Color
                </label>
                <ColorPicker
                  color={content?.titleColor || "#5c3618"}
                  onChange={(color) => updateContent("titleColor", color)}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Highlight Color
                </label>
                <ColorPicker
                  color={content?.highlightColor || "#d17b34"}
                  onChange={(color) => updateContent("highlightColor", color)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Subtitle Color
                </label>
                <ColorPicker
                  color={content?.subtitleColor || "#6b5c4d"}
                  onChange={(color) => updateContent("subtitleColor", color)}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Primary Button Background
                </label>
                <ColorPicker
                  color={content?.primaryButtonBackgroundColor || "#d17b34"}
                  onChange={(color) =>
                    updateContent("primaryButtonBackgroundColor", color)
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Primary Button Text
                </label>
                <ColorPicker
                  color={content?.primaryButtonTextColor || "#ffffff"}
                  onChange={(color) =>
                    updateContent("primaryButtonTextColor", color)
                  }
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Secondary Button Border
                </label>
                <ColorPicker
                  color={content?.secondaryButtonBorderColor || "#d17b34"}
                  onChange={(color) =>
                    updateContent("secondaryButtonBorderColor", color)
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Secondary Button Text
                </label>
                <ColorPicker
                  color={content?.secondaryButtonTextColor || "#d17b34"}
                  onChange={(color) =>
                    updateContent("secondaryButtonTextColor", color)
                  }
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Secondary Button Hover Background
                </label>
                <ColorPicker
                  color={
                    content?.secondaryButtonHoverBackgroundColor || "#d17b34"
                  }
                  onChange={(color) =>
                    updateContent("secondaryButtonHoverBackgroundColor", color)
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Secondary Button Hover Text
                </label>
                <ColorPicker
                  color={content?.secondaryButtonHoverTextColor || "#ffffff"}
                  onChange={(color) =>
                    updateContent("secondaryButtonHoverTextColor", color)
                  }
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Hours Badge Background
                </label>
                <ColorPicker
                  color={content?.hoursBadgeBackgroundColor || "#ffffff"}
                  onChange={(color) =>
                    updateContent("hoursBadgeBackgroundColor", color)
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Hours Badge Text
                </label>
                <ColorPicker
                  color={content?.hoursBadgeTextColor || "#5c3618"}
                  onChange={(color) =>
                    updateContent("hoursBadgeTextColor", color)
                  }
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Hours Badge Highlight
                </label>
                <ColorPicker
                  color={content?.hoursBadgeHighlightColor || "#d17b34"}
                  onChange={(color) =>
                    updateContent("hoursBadgeHighlightColor", color)
                  }
                />
              </div>
            </div>
          </div>

          {/* Overlay Card Settings */}
          <div className="bg-gray-50 p-4 rounded-md">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium">Overlay Card Settings</h4>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={content?.overlayCardVisible !== false}
                    onChange={(e) =>
                      updateContent("overlayCardVisible", e.target.checked)
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Show Overlay Card</span>
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Card Title
              </label>
              <input
                type="text"
                value={content?.overlayCardTitle || "Try Our Specialty"}
                onChange={(e) =>
                  updateContent("overlayCardTitle", e.target.value)
                }
                className="w-full p-2 border rounded"
              />
            </div>

            {/* <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Card Text</label>
              <input
                type="text"
                value={content?.overlayCardText || 'Mix Berry Coffee Cake fresh throughout the day'}
                onChange={(e) => updateContent('overlayCardText', e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div> */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Card Text
              </label>
              <textarea
                value={
                  content?.overlayCardText ||
                  "Mix Berry Coffee Cake fresh throughout the day"
                }
                onChange={(e) =>
                  updateContent("overlayCardText", e.target.value)
                }
                className="w-full p-2 border rounded"
                rows="4"
                placeholder="You can use HTML tags like <p>, <ul>, <li>, <br>, etc."
              />
              <p className="text-xs text-gray-500 mt-1">
                HTML tags like &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;br&gt; are
                supported
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Card Tagline
              </label>
              <input
                type="text"
                value={content?.overlayCardTagline || "→ Featured Today"}
                onChange={(e) =>
                  updateContent("overlayCardTagline", e.target.value)
                }
                className="w-full p-2 border rounded"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Card Background Color
                </label>
                <ColorPicker
                  color={
                    content?.overlayCardBackgroundColor ||
                    "rgba(255, 255, 255, 0.9)"
                  }
                  onChange={(color) =>
                    updateContent("overlayCardBackgroundColor", color)
                  }
                  allowAlpha={true}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Card Title Color
                </label>
                <ColorPicker
                  color={content?.overlayCardTitleColor || "#5c3618"}
                  onChange={(color) =>
                    updateContent("overlayCardTitleColor", color)
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Card Text Color
                </label>
                <ColorPicker
                  color={content?.overlayCardTextColor || "#6b5c4d"}
                  onChange={(color) =>
                    updateContent("overlayCardTextColor", color)
                  }
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Card Tagline Color
                </label>
                <ColorPicker
                  color={content?.overlayCardTaglineColor || "#d17b34"}
                  onChange={(color) =>
                    updateContent("overlayCardTaglineColor", color)
                  }
                />
              </div>
            </div>
          </div>

          {/* Category Section Settings */}
          <div className="bg-gray-50 p-4 rounded-md">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium">Categories Section</h4>
              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={content?.showCategories !== false}
                    onChange={(e) =>
                      updateContent("showCategories", e.target.checked)
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Show Categories</span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Category Background
                </label>
                <ColorPicker
                  color={content?.categoryBackgroundColor || "#5c3618"}
                  onChange={(color) =>
                    updateContent("categoryBackgroundColor", color)
                  }
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Category Text Color
                </label>
                <ColorPicker
                  color={content?.categoryTextColor || "#ffffff"}
                  onChange={(color) =>
                    updateContent("categoryTextColor", color)
                  }
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Icon Background Color
                </label>
                <ColorPicker
                  color={content?.categoryIconBackgroundColor || "#ffffff"}
                  onChange={(color) =>
                    updateContent("categoryIconBackgroundColor", color)
                  }
                />
              </div>
            </div>

            {/* Category Items */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium">
                  Category Items
                </label>
                <button
                  type="button"
                  onClick={addCategoryItem}
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                >
                  Add Item
                </button>
              </div>

              {(content?.categoryItems || []).map((item, index) => (
                <div key={index} className="mb-4 p-3 border rounded bg-white">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-sm">
                      Item {index + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeCategoryItem(index)}
                      className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                    >
                      Remove
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={item.name || ""}
                        onChange={(e) =>
                          handleCategoryChange(index, "name", e.target.value)
                        }
                        className="w-full p-2 border rounded text-sm"
                        placeholder="Category Name"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium mb-1">
                        Link
                      </label>
                      <input
                        type="text"
                        value={item.link || ""}
                        onChange={(e) =>
                          handleCategoryChange(index, "link", e.target.value)
                        }
                        className="w-full p-2 border rounded text-sm"
                        placeholder="/category/url"
                      />
                    </div>
                  </div>

                  <div className="mt-2">
                    <label className="block text-xs font-medium mb-1">
                      Image URL
                    </label>
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={item.imageUrl || ""}
                        onChange={(e) =>
                          handleCategoryChange(
                            index,
                            "imageUrl",
                            e.target.value
                          )
                        }
                        className="w-full p-2 border rounded text-sm"
                        placeholder="Image URL"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setCurrentImageField(
                            `categoryItems[${index}].imageUrl`
                          );
                          setCurrentMediaType("image");
                          setShowMediaLibrary(true);
                        }}
                        className="ml-2 px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-xs"
                      >
                        Browse
                      </button>
                    </div>
                    {item.imageUrl && (
                      <div className="mt-2 relative h-10 w-10 overflow-hidden rounded border">
                        <img
                          src={item.imageUrl}
                          alt={`Category ${index + 1}`}
                          className="h-full w-full object-contain"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {(!content?.categoryItems ||
                content.categoryItems.length === 0) && (
                <div className="text-center p-3 bg-gray-100 rounded">
                  <p className="text-sm text-gray-500">
                    No category items added yet. Click "Add Item" to create one.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Layout Settings */}
          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-medium mb-4">Layout Settings</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Left Column Width
                </label>
                <select
                  value={content?.leftColumnWidth || "1/2"}
                  onChange={(e) =>
                    updateContent("leftColumnWidth", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                >
                  <option value="1/2">1/2 (50%)</option>
                  <option value="1/3">1/3 (33%)</option>
                  <option value="2/3">2/3 (67%)</option>
                  <option value="1/4">1/4 (25%)</option>
                  <option value="3/4">3/4 (75%)</option>
                  <option value="0">0 (Hide)</option>
                  <option value="full">Full (100%)</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Right Column Width
                </label>
                <select
                  value={content?.rightColumnWidth || "1/2"}
                  onChange={(e) =>
                    updateContent("rightColumnWidth", e.target.value)
                  }
                  className="w-full p-2 border rounded"
                >
                  <option value="1/2">1/2 (50%)</option>
                  <option value="1/3">1/3 (33%)</option>
                  <option value="2/3">2/3 (67%)</option>
                  <option value="1/4">1/4 (25%)</option>
                  <option value="3/4">3/4 (75%)</option>
                  <option value="0">0 (Hide)</option>
                  <option value="full">Full (100%)</option>
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.enableAnimations !== false}
                  onChange={(e) =>
                    updateContent("enableAnimations", e.target.checked)
                  }
                  className="mr-2"
                />
                <span className="text-sm">Enable Animations</span>
              </label>
            </div>

            {/* Add this after the layout settings section */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Content Alignment (when left is 100%)
              </label>
              <select
                value={content?.contentAlignment || "left"}
                onChange={(e) =>
                  updateContent("contentAlignment", e.target.value)
                }
                className="w-full p-2 border rounded"
              >
                <option value="left">Left Aligned</option>
                <option value="center">Center Aligned</option>
              </select>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HeaderDividedForm;
